import React, { useEffect, useRef, useState } from 'react';
// Background image import removed to avoid module resolution issues; use public path instead
import { Mail, Phone, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

export function ContactPage() {
  const heroRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const $ = (window as any).jQuery as any;
    const pg = (window as any).particleground as any;
    if (!heroRef.current || !$.fn || !pg) return;
    const $el = (window as any).jQuery(heroRef.current);
    if ($el.data('pg-init')) return;
    $el.data('pg-init', true);
    $el.particleground({
      dotColor: 'rgba(93, 101, 107, 0.35)',
      lineColor: 'rgba(93, 101, 107, 0.35)',
      parallax: true,
      parallaxMultiplier:50
    });
    return () => { try { $el.particleground('destroy'); } catch {} };
  }, []);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const to = 'info@ilimera.com';
    const subject = formData.subject || 'İletişim Formu: Yeni Mesaj';
    const body = `Ad Soyad: ${formData.name}\nE-posta: ${formData.email}\nTelefon: ${formData.phone}\nKonu: ${formData.subject}\n\nMesaj:\n${formData.message}`;
    const mailtoUrl = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 3000);
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Genel Merkez',
      content: 'Akdeniz Üniversitesi, Teknokent Uluğbey ArGe-2 No:3A/B96 Konyaaltı / ANTALYA',
      subContent: '07070 Türkiye'
    },
    {
      icon: Phone,
      title: 'Telefon',
      content: '+90 (533) 547 85 27',
      //subContent: '+90 (536) 555-0123 (Mobil)'
    },
    {
      icon: Mail,
      title: 'E-posta',
      content: 'alpaslanyildizhan@ilimera.com',
      //subContent: 'destek@ilimera.com'
    },
    {
      icon: Clock,
      title: 'Çalışma Saatleri',
      content: (
        <>
          Pazartesi - Cumaretsi<br/> 08:00 - 18:00
        </>
      )
      //subContent: 'Cumartesi: 09:00 - 14:00'
    },
    {
      icon: MapPin,
      title: 'Ar-Ge Merkezi',
      content: (
        <>
          Davraz Mah. 104. Cadde No:44-A Kat:2 Ofis:13/1<br />
          Merkez / ISPARTA
        </>
      ),
      subContent: '32300 Türkiye'
    },
    {
      icon: Phone,
      title: 'Telefon',
      content: '+90 (542) 549 85 27',
      //subContent: '+90 (536) 555-0123 (Mobil)'
    },
    {
      icon: Mail,
      title: 'E-posta',
      content: 'info@ilimera.com',
      //subContent: 'destek@ilimera.com'
    },
    {
      icon: Clock,
      title: 'Çalışma Saatleri',
      content:(
        <>
          Pazartesi - Cumaretsi<br/> 08:00 - 18:00
        </>
      )
      //subContent: 'Cumartesi: 09:00 - 14:00'
    },
  ];

  const subjects = [
    'Genel Bilgi',
    'Proje Teklifi',
    'Teknik Destek',
    'Satış Danışmanlığı',
    'İş Birliği',
    'Diğer'
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-16 pb-16 lg:pt-20 lg:pb-20 overflow-hidden">
        {/* Background image */}
        <div aria-hidden className="absolute inset-0 z-0 bg-cover bg-center" style={{ backgroundImage: 'url(/cta3-bg.jpg)' }}></div>
        {/* Dark overlay */}
        <div aria-hidden className="absolute inset-0 z-[5] bg-black/45"></div>
        {/* Particles above overlay */}
        <div ref={heroRef} className="absolute inset-0 z-10 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          <div className="text-center">
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              İletişime Geçin
            </h1>
            <p className="text-lg text-white max-w-3xl mx-auto">
              Projeleriniz için teknolojik çözümler geliştirmek üzere 
              uzman ekibimizle iletişime geçin. Size en uygun çözümü birlikte bulalım.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {contactInfo.map((info, index) => (
              <Card
                key={index}
                className="group relative text-center p-6 rounded-xl bg-white/70 dark:bg-gray-900/60 backdrop-blur-md border border-white/20 dark:border-white/10 shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(34,197,94,0.25)] ring-1 ring-transparent hover:ring-primary/40"
              >
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/40 dark:to-emerald-900/40 ring-1 ring-primary/20 group-hover:ring-primary/50 shadow-inner transition-all duration-300 group-hover:shadow-[0_0_20px_rgba(34,197,94,0.35)]">
                  <info.icon className="text-primary" size={24} />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2 transition-colors duration-300 group-hover:text-foreground">
                  {info.title}
                </h3>
                <p className="text-foreground dark:text-white mb-1">
                  {info.content}
                </p>
                {info.subContent && (
                  <p className="text-sm text-muted-foreground dark:text-white">
                    {info.subContent}
                  </p>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Map Genel Merkez */}
            <div>
              <h2 className="text-3xl font-bold text-foreground mb-6">
                Genel Merkez
              </h2>
              <Card className="overflow-hidden">
                <CardContent className="p-3">
                  <iframe
                    title="İlimera Teknoloji Konum"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d379.6102909839789!2d30.645848351230985!3d36.90001355778898!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14c391d4afe6ac4b%3A0x8f9d88b2dd970272!2sAntalya%20Teknokent!5e1!3m2!1str!2str!4v1757161382015!5m2!1str!2str"
                    width="100%"
                    height="400"
                    style={{ border: 0 }}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                    className="block w-full h-[520px] md:h-[640px] lg:h-[720px]"
                  />
                </CardContent>
              </Card>
            </div>
            {/* Map Ar-Ge Merkezi */}
            <div>
              <h2 className="text-3xl font-bold text-foreground mb-6">
                Ar-Ge Merkezi
              </h2>
              <Card className="overflow-hidden">
                <CardContent className="p-3">
                  <iframe
                    title="İlimera Teknoloji Konum"
                    src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d2077.7865941548534!2d30.561338!3d37.775119!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14c5b5d01fddfdd7%3A0x35ce887d39fd722c!2zxLBMxLBNRVJBIFRFS05PTE9KxLAgWUFaSUxJTSBMxLBNxLBURUQgxZ7EsFJLRVTEsA!5e1!3m2!1str!2str!4v1758870130998!5m2!1str!2str"
                    width="100%"
                    height="400"
                    style={{ border: 0 }}
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                    className="block w-full h-[520px] md:h-[640px] lg:h-[720px]"
                  />
                </CardContent>
              </Card>
            </div>

          {/* Contact Form */}
          <div className="mt-8">
            <h2 className="text-3xl font-bold text-foreground mb-6">
              Mesaj Gönderin
            </h2>
              <Card>
                <CardContent className="p-6">
                  {isSubmitted ? (
                    <div className="text-center py-8">
                      <CheckCircle className="w-16 h-16 text-primary mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-foreground mb-2">
                        Mesajınız Alındı!
                      </h3>
                      <p className="text-muted-foreground">
                        En kısa sürede size geri dönüş yapacağız.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="name">Ad Soyad *</Label>
                          <Input
                            id="name"
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            placeholder="Adınız ve soyadınız"
                          />
                        </div>
                        <div>
                          <Label htmlFor="email">E-posta *</Label>
                          <Input
                            id="email"
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) => handleChange('email', e.target.value)}
                            placeholder="ornek@email.com"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="phone">Telefon</Label>
                          <Input
                            id="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => handleChange('phone', e.target.value)}
                            placeholder="+90 5XX XXX XX XX"
                          />
                        </div>
                        <div>
                          <Label htmlFor="subject">Konu *</Label>
                          <Select value={formData.subject} onValueChange={(value) => handleChange('subject', value)} required>
                            <SelectTrigger>
                              <SelectValue placeholder="Bir konu seçin" />
                            </SelectTrigger>
                            <SelectContent>
                              {subjects.map((subject) => (
                                <SelectItem key={subject} value={subject}>
                                  {subject}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="message">Mesaj *</Label>
                        <Textarea
                          id="message"
                          required
                          rows={6}
                          value={formData.message}
                          onChange={(e) => handleChange('message', e.target.value)}
                          placeholder="Lütfen projeniz hakkında detaylı bilgi verin..."
                          className="resize-none"
                        />
                      </div>

                      <div className="text-sm text-muted-foreground">
                        Bu formu göndererek
                        {' '}<a href="/kvkk-iletisim-aydinlatma" className="underline text-primary" target="_blank" rel="noopener">KVKK Aydınlatma Metni</a>
                        {' '}hakkında bilgilendirildiğimi kabul ederim.
                      </div>

                      <Button type="submit" size="lg" className="w-full bg-primary hover:bg-primary/90">
                        <Send className="mr-2" size={20} />
                        Mesaj Gönder
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:!text-white ss-title mb-4" style={{ color: 'var(--foreground)' }}>
              Sıkça Sorulan Sorular
            </h2>
            <p className="text-lg text-gray-600 dark:!text-white ss-desc" style={{ color: 'var(--foreground)' }}>
              En çok merak edilen sorular ve cevapları
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:!text-white mb-3" style={{ color: 'var(--foreground)' }}>
                Proje geliştirme süreci nasıl işliyor?
              </h3>
              <p className="text-gray-600 dark:!text-white" style={{ color: 'var(--foreground)' }}>
                İlk görüşmede ihtiyaçlarınızı analiz ediyor, teknik çözüm önerisi sunuyoruz. 
                Onay sonrası tasarım, geliştirme, test ve kurulum aşamaları takip ediyor.
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:!text-white mb-3" style={{ color: 'var(--foreground)' }}>
                Garanti süresi ne kadar?
              </h3>
              <p className="text-gray-600 dark:!text-white" style={{ color: 'var(--foreground)' }}>
                Tüm ürünlerimiz için 2 yıl garantiimiz bulunmaktadır. 
                Garanti kapsamında teknik destek ve arıza giderme hizmetleri ücretsizdir.
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:!text-white mb-3" style={{ color: 'var(--foreground)' }}>
                Kurulum ve eğitim hizmeti veriyor musunuz?
              </h3>
              <p className="text-gray-600 dark:!text-white" style={{ color: 'var(--foreground)' }}>
                Evet, tüm ürünlerimizde kurulum ve kullanıcı eğitimi hizmetimiz bulunmaktadır. 
                Sistemin etkin kullanımı için gerekli tüm eğitimleri sağlıyoruz.
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:!text-white mb-3" style={{ color: 'var(--foreground)' }}>
                Özel ürün geliştirme yapıyor musunuz?
              </h3>
              <p className="text-gray-600 dark:!text-white" style={{ color: 'var(--foreground)' }}>
                Tabii ki! Müşteri ihtiyaçlarına özel çözümler geliştirmek uzmanlık alanımızdır. 
                Mevcut ürünlerimizi de özel ihtiyaçlara göre uyarlayabiliriz.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-black mb-4">
            Hemen Başlayalım!
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Projenizi hayata geçirmek için doğru adrestesiniz. 
            Ücretsiz danışmanlık için bugün bizimle iletişime geçin.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+905425498527">
              <Button size="lg" variant="outline" className="border-primary text-primary dark:bg-gray-800 dark:hover:bg-gray-800 hover:bg-primary hover:text-white">
                <Phone className="mr-2" size={20} />
                Hemen Arayın
              </Button>
            </a>
            <a href="mailto:info@ilimera.com">
              <Button size="lg" variant="outline" className="dark:bg-gray-800 border-primary text-primary dark:hover:bg-gray-800 hover:bg-primary hover:text-white">
                <Mail className="mr-2" size={20} />
                E-posta Gönderin
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}